import { t } from '@lingui/core/macro';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { queryClient } from 'api';
import MotionLazyContainer from 'components/animate/MotionLazyContainer';
import { ProgressBarStyle } from 'components/ProgressBar';
import { StrongAuthenticationGate } from 'components/strongAuthentication/StrongAuthenticationGate';
import { useCurrentRole, useCurrentUserAndValidateRoles, useIsTrueUserAdmin } from 'hooks/slices';
import { ExportCenter } from 'pages/shared/Exports/ExportCenter';
import { StrictMode, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { router } from 'routes';
import { useAuthStore } from 'stores/authStore';
import FaksThemeProvider from 'theme/FaksThemeProvider';
import { getIntercomSettings } from 'utils/intercom';
import { useInitializeTracking, useInitializeUserTracking } from 'utils/tracking';
import intercomImplementation from './intercomImplementation';
import 'react-toastify/dist/ReactToastify.css';
import 'yet-another-react-lightbox/styles.css';
import './assets/css/toastify.css';
import './utils/yupCustomMethods';

export default function App() {
  const currentUserId = useAuthStore((state) => state.currentUserId);
  const isTrueUserAdmin = useIsTrueUserAdmin();
  const { currentUser, trueUserId } = useCurrentUserAndValidateRoles() || {};
  const currentRole = useCurrentRole();

  useEffect(() => {
    // Browser support warning.
    // In App.tsx to display the toast only once at the load of the app.
    // For now, only based on presence of IntersectionObserver. Add condition here if needed.
    if (!('IntersectionObserver' in window)) {
      toast.error(
        t`Vous utilisez une version d’un navigateur qui n’est pas supporté. Certaines fonctionnalités pourraient ne pas fonctionner.`,
        { toastId: 'unsupportedBrowserToast' }
      );
      return;
    }
  }, []);

  // INTERCOM INIT
  useEffect(() => {
    intercomImplementation();

    if (isTrueUserAdmin) {
      window.Intercom?.('shutdown');
    } else if (currentUser) {
      window.Intercom?.('boot', getIntercomSettings(currentUser, currentRole));
    } else {
      window.Intercom?.('boot', { app_id: import.meta.env.REACT_APP_INTERCOM_APP_ID });
    }
  }, [isTrueUserAdmin, currentUser, currentRole]);

  // SENTRY INIT
  useEffect(() => {
    if (currentUserId) Sentry.setUser({ id: currentUserId.toString() });
    if (trueUserId) Sentry.setTags({ true_user_id: trueUserId });
  }, [currentUserId, trueUserId]);

  useInitializeTracking();
  useInitializeUserTracking();

  return (
    <QueryClientProvider client={queryClient}>
      <MotionLazyContainer>
        <FaksThemeProvider>
          <ProgressBarStyle />
          <StrictMode>
            <RouterProvider router={router} />
          </StrictMode>
          <ToastContainer closeOnClick />
          <ExportCenter />
          <StrongAuthenticationGate />
        </FaksThemeProvider>
      </MotionLazyContainer>
    </QueryClientProvider>
  );
}
